import CustomAutocomplete from "@/components/Form/InputFields/CustomAutocomplete";
import { TName } from "@/types";
import { Box, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import { FieldValues, PathValue, useWatch } from "react-hook-form";
import { IFormProps } from "../../targetingExpression.interface";

export default function SelectModuleWithType<T extends FieldValues>({
  itemList,
  errors,
  control,
  name,
  setValue,
  getValues,
  label,
  loading,
  isReadOnly = false
}: SelectModuleWithTypeProps<T>) {
  const [value, includeOrExclude] = useWatch({
    control,
    name: [`${name}.value` as TName<T>, `${name}.type` as TName<T>],
  });

  const onValueChange = () => {
    const [value, includeOrExclude] = getValues([`${name}.value`, `${name}.type`] as TName<T>[])
    if (value?.length && !includeOrExclude) {
      setValue(`${name}.type` as TName<T>, "include" as PathValue<T, TName<T>>, { shouldDirty: true });
    }
    if (!value?.length && includeOrExclude) {
      setValue(`${name}.type` as TName<T>, null as PathValue<T, TName<T>>, { shouldDirty: true });
    }
  }


  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        p: 2,
        backgroundColor: "background.paper",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          {label}
        </Typography>
        {value?.length > 0 && (
          <FormControl size="small">
            <RadioGroup
              row
              value={includeOrExclude || "include"}
              onChange={(e) => setValue(`${name}.type` as TName<T>, e.target.value as PathValue<T, TName<T>>, { shouldDirty: true })}
            >
              <FormControlLabel
                value="include"
                control={<Radio size="small" readOnly={isReadOnly} />}
                label="Include"
                sx={{
                  mr: 2,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                    color: includeOrExclude === "include" ? "success.main" : "text.secondary"
                  }
                }}
                disabled={isReadOnly}
              />
              <FormControlLabel
                value="exclude"
                control={<Radio size="small" readOnly={isReadOnly} />}
                label="Exclude"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                    color: includeOrExclude === "exclude" ? "error.main" : "text.secondary"
                  }
                }} disabled={isReadOnly}
              />
            </RadioGroup>
          </FormControl>
        )}
      </Box>
      <CustomAutocomplete<T>
        name={`${name}.value` as TName<T>}
        label=""
        control={control}
        itemList={itemList as any}
        error={errors?.advertiserId?.message}
        loading={loading}
        readOnly={isReadOnly || loading}
        type="secondary"
        onChange={onValueChange}
        isMultiple
      />
    </Box>
  );
}

interface SelectModuleWithTypeProps<T extends FieldValues>
  extends Omit<IFormProps<T>, "isSubmitting"> {
  name: string;
  itemList: any;
  label: string;
  loading: boolean;
  isReadOnly?: boolean
}
